import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonAddressBooks,
  CommonScrollIntoView,
  CommonRequired,
  CommonOnError,
  CommonOnUpdate,
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import {
  ApplicableModel,
  CountryArrayModel, CountryModel, DateModel, LocationModel
} from 'forms/shared/types';
import { Dispatch } from 'redux';
import { ForeignContactsItemModel } from 'forms/sections/foreign/contacts/types';
import Field from 'components/Form/Field';
import Textarea from 'components/Form/Textarea';
import DateControl from 'components/Form/DateControl';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import BranchCollection from 'components/Form/BranchCollection';
import Country from 'components/Form/Country';
import Text from 'components/Form/Text';
import { AccordionItemProps } from 'components/Form/Accordion/types';
import PVQName from 'components/Form/PVQName';
import {
  foreignContactFrequencyOptions,
  foreignGovernmentServiceOptions,
} from 'constants/enums/foreignActivityOptions';
import PVQLocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import { EmploymentForeignServiceContact, EmploymentForeignServiceItemModel } from 'forms/sections/history/employment/types';
import DateRange from 'components/Form/DateRange';
import { isSafeInternational } from 'helpers/locations';

interface Props extends Partial<EmploymentForeignServiceItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  applicantBirthdate: DateModel
  dispatch: Dispatch
  formType?: string
}

export default class EmploymentForeignService extends ValidationElement<Props> {
  constructor(props) {
    super(props);
  }

  handleNameError = (value: any, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code: err.code.includes('required')
        ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
        : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (queue: Partial<ForeignContactsItemModel>) => {
    this.props.onUpdate({
      Discipline: this.props.Discipline,
      DisciplineOtherExplanation: this.props.DisciplineOtherExplanation,
      Country: this.props.Country,
      Agency: this.props.Agency,
      CivilianOrMilitary: this.props.CivilianOrMilitary,
      HighestRank: this.props.HighestRank,
      Department: this.props.Department,
      HowAssociated: this.props.HowAssociated,
      DatesServed: this.props.DatesServed,
      ReasonLeft: this.props.ReasonLeft,
      HasMaintainedContact: this.props.HasMaintainedContact,
      ForeignContacts: this.props.ForeignContacts,
      ...queue,
    });
  };

  updateDiscipline = (values: EmploymentForeignServiceItemModel['Discipline']) => {
    this.update({
      Discipline: values,
      DisciplineOtherExplanation: values?.value === 'Other' ? this.props.DisciplineOtherExplanation : {}
    });
  };

  updateDisciplineOtherExplanation = (values: EmploymentForeignServiceItemModel['DisciplineOtherExplanation']) => {
    this.update({
      DisciplineOtherExplanation: values,
    });
  };

  updateCountry = (value: EmploymentForeignServiceItemModel['Country']) => {
    this.update({
      Country: value,
    });
  };

  updateAgency = (value: EmploymentForeignServiceItemModel['Agency']) => {
    this.update({
      Agency: value,
    });
  };

  updateCivilianOrMilitary = (value: EmploymentForeignServiceItemModel['CivilianOrMilitary']) => {
    this.update({
      CivilianOrMilitary: value,
    });
  };

  updateHighestRank = (value: EmploymentForeignServiceItemModel['HighestRank']) => {
    this.update({
      HighestRank: value,
    });
  };

  updateDepartment = (value: EmploymentForeignServiceItemModel['Department']) => {
    this.update({
      Department: value,
    });
  };

  updateHowAssociated = (value: EmploymentForeignServiceItemModel['HowAssociated']) => {
    this.update({
      HowAssociated: value,
    });
  };

  updateDatesServed = (values: EmploymentForeignServiceItemModel['DatesServed']) => {
    this.update({
      DatesServed: values,
      ReasonLeft: values?.present === true ? {} : this.props.ReasonLeft,
    });
  };

  updateReasonLeft = (value: EmploymentForeignServiceItemModel['ReasonLeft']) => {
    this.update({
      ReasonLeft: value,
    });
  };

  updateForeignContacts = (value: EmploymentForeignServiceItemModel['ForeignContacts']) => {
    this.update({
      ForeignContacts: value,
    });
  };

  render() {
    const attribute = `ForeignServiceList.items[${this.props.$index}].Item`;

    return (
      <div className="history-foreign-service">
        <Field
          title={i18n.t('history.pvq.employment.heading.foreignDiscipline')}
          className="foreign-discipline"
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Discipline`}
        >
          <RadioGroup
            className="Discipline option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={this.props.Discipline?.value}
            attribute={`${attribute}.Discipline`}
          >
            {foreignGovernmentServiceOptions.map((option) => (
              <Radio
                key={option}
                name={option}
                label={option}
                value={option}
                className={option}
                onUpdate={this.updateDiscipline}
                onError={this.props.onError}
              />
            ))}
          </RadioGroup>
        </Field>

        {this.props.Discipline?.value === 'Other'
          && (
            <Field
              title={i18n.t('history.pvq.employment.heading.explanation')}
              titleSize="label"
              adjustFor="textarea"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.DisciplineOtherExplanation`}
            >
              <Textarea
                {...this.props.DisciplineOtherExplanation}
                name="DisciplineOtherExplanation"
                className="frequency-explanation"
                onUpdate={this.updateDisciplineOtherExplanation}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.DisciplineOtherExplanation`}
              />
            </Field>
          )}

        <Field
          title={i18n.t('history.pvq.employment.heading.country')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Country`}
        >
          <Country
            {...this.props.Country}
            name="Country"
            className="country"
            onUpdate={this.updateCountry}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Country`}
            excludeUnitedStates
          />
        </Field>

        <Field
          title={i18n.t('history.pvq.employment.heading.agency')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Agency`}
        >
          <Text
            {...this.props.Agency}
            name="Agency"
            className="agency"
            onUpdate={this.updateAgency}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Agency`}
          />
        </Field>

        <Field
          title={i18n.t('history.pvq.employment.heading.civilianOrMilitary')}
          className="civilian-or-military"
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CivilianOrMilitary`}
        >
          <RadioGroup
            className="civilian-or-military option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={this.props.CivilianOrMilitary?.value}
            attribute={`${attribute}.CivilianOrMilitary`}
          >
            <Radio
              name="Civilian"
              label="Civilian"
              value="Civilian"
              className="civilian"
              onUpdate={this.updateCivilianOrMilitary}
              onError={this.props.onError}
            />
            <Radio
              name="Military member"
              label="Military member"
              value="Military member"
              className="military-member"
              onUpdate={this.updateCivilianOrMilitary}
              onError={this.props.onError}
            />
          </RadioGroup>
        </Field>

        <Field
          title={i18n.t('history.pvq.employment.heading.rank')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.HighestRank`}
        >
          <Text
            {...this.props.HighestRank}
            name="HighestRank"
            className="rank"
            onUpdate={this.updateHighestRank}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.HighestRank`}
          />
        </Field>

        <Field
          title={i18n.t('history.pvq.employment.heading.department')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Department`}
        >
          <Text
            {...this.props.Department}
            name="Department"
            className="department"
            onUpdate={this.updateDepartment}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Department`}
          />
        </Field>

        <Field
          title={i18n.t('history.pvq.employment.heading.howAssociated')}
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.HowAssociated`}
        >
          <Textarea
            {...this.props.HowAssociated}
            name="HowAssociated"
            className="how-associated"
            onUpdate={this.updateHowAssociated}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.HowAssociated`}
          />
        </Field>

        <Field
          title={i18n.t('history.pvq.employment.heading.datesServed')}
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.DatesServed`}
        >
          <DateRange
            name="DatesServed"
            {...this.props.DatesServed}
            onUpdate={this.updateDatesServed}
            onError={this.props.onError}
            minDateEqualTo
            className="dates-served"
            required={this.props.required}
            hideFromDay
            hideToDay
            allowPresent
            attribute={`${attribute}.DatesServed`}
          />
        </Field>

        {this.props.DatesServed?.present !== true
          && (
            <Field
              title={i18n.t('history.pvq.employment.heading.reasonLeft')}
              adjustFor="textarea"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.ReasonLeft`}
            >
              <Textarea
                {...this.props.ReasonLeft}
                name="ReasonLeft"
                className="reason-left"
                onUpdate={this.updateReasonLeft}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.ReasonLeft`}
              />
            </Field>
          )}

        <BranchCollection
          {...this.props.ForeignContacts}
          label={i18n.t('history.pvq.employment.heading.foreignContact')}
          appendLabel={i18n.t('history.pvq.employment.heading.foreignContactAppend')}
          className="foreign-contacts"
          onUpdate={this.updateForeignContacts}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.ForeignContacts`}
        >
          <EmploymentForeignContactItem
            bind
            name="Item"
            handleNameError={this.handleNameError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ForeignContacts`}
          />
        </BranchCollection>
      </div>
    );
  }
}

interface EmploymentForeignContactItemProps extends AccordionItemProps, EmploymentForeignServiceContact {
  scrollIntoView?: CommonScrollIntoView
  required?: CommonRequired
  attribute?: string
  onUpdate?: CommonOnUpdate
  handleNameError: any
  onError?: CommonOnError
}

const EmploymentForeignContactItem: React.FC<EmploymentForeignContactItemProps> = function ({
  scrollIntoView,
  required,
  attribute,
  onError,
  handleNameError,
  $index,
  onUpdate,
  Name = {},
  Rank = {},
  DateMet = {},
  DateLastContacted = {},
  Frequency = {},
  FrequencyOtherExplanation = {},
  Address = {},
  InUsaNotApplicable = { applicable: true },
  Relationship = {},
}) {
  const itemAttribute = `${attribute}.items[${$index}].Item`;

  const updateName = (values: EmploymentForeignServiceContact['Name']) => {
    onUpdate({ Name: values });
  };

  const updateRank = (values: EmploymentForeignServiceContact['Rank']) => {
    onUpdate({ Rank: values });
  };

  const updateDateMet = (values: EmploymentForeignServiceContact['DateMet']) => {
    onUpdate({ DateMet: values });
  };

  const updateDateLastContacted = (values: EmploymentForeignServiceContact['DateLastContacted']) => {
    onUpdate({ DateLastContacted: values });
  };

  const updateFrequency = (values: EmploymentForeignServiceContact['Frequency']) => {
    onUpdate({
      Frequency: values,
      FrequencyOtherExplanation: values.value === 'Other' ? (FrequencyOtherExplanation || {}) : {}
    });
  };

  const updateFrequencyOtherExplanation = (values: EmploymentForeignServiceContact['FrequencyOtherExplanation']) => {
    onUpdate({ FrequencyOtherExplanation: values });
  };

  const updateAddress = (values: EmploymentForeignServiceContact['Address']) => {
    onUpdate({
      Address: values,
    });
  };

  const updateInUsaNotApplicablePvq = (
    inUsaNotApplicable: ApplicableModel
  ) => {
    onUpdate({
      InUsaNotApplicable: inUsaNotApplicable,
      Address: {},
    });
  };

  const updateRelationship = (values: EmploymentForeignServiceContact['Relationship']) => {
    onUpdate({
      Relationship: values,
    });
  };

  return (
    <>
      <Field
        title={i18n.t('history.pvq.employment.heading.foreignName')}
        optional
        scrollIntoView={scrollIntoView}
      >
        <PVQName
          {...Name}
          name="Name"
          onUpdate={updateName}
          required={required}
          scrollIntoView={scrollIntoView}
          onError={handleNameError}
          attribute={`${itemAttribute}.Name`}
          hideMiddleName
        />
      </Field>

      <Field
        title={i18n.t('history.pvq.employment.heading.foreignContactRank')}
        scrollIntoView={scrollIntoView}
        attribute={`${itemAttribute}.Rank`}
      >
        <Text
          {...Rank}
          name="Rank"
          className="rank"
          onUpdate={updateRank}
          onError={onError}
          required={required}
          attribute={`${itemAttribute}.Rank`}
        />
      </Field>

      <Field
        title={i18n.t('history.pvq.employment.heading.dateMet')}
        adjustFor="datecontrol"
        scrollIntoView={scrollIntoView}
        attribute={`${itemAttribute}.DateMet`}
      >
        <DateControl
          name="DateMet"
          {...DateMet}
          onUpdate={updateDateMet}
          onError={onError}
          className="date-met"
          required={required}
          hideDay
          attribute={`${itemAttribute}.DateMet`}
        />
      </Field>

      <Field
        title={i18n.t('history.pvq.employment.heading.dateLastContacted')}
        adjustFor="datecontrol"
        scrollIntoView={scrollIntoView}
        attribute={`${itemAttribute}.DateLastContacted`}
      >
        <DateControl
          name="DateLastContacted"
          {...DateLastContacted}
          onUpdate={updateDateLastContacted}
          onError={onError}
          className="date-last-contacted"
          required={required}
          hideDay
          attribute={`${itemAttribute}.DateLastContacted`}
        />
      </Field>

      <Field
        title={i18n.t('history.pvq.employment.heading.frequency')}
        className="foreign-contact-frequency"
        adjustFor="big-buttons"
        scrollIntoView={scrollIntoView}
        attribute={`${itemAttribute}.Frequency`}
      >
        <RadioGroup
          className="frequency option-list option-list-vertical"
          required={required}
          onError={onError}
          selectedValue={Frequency?.value}
          attribute={`${itemAttribute}.Frequency`}
        >
          {foreignContactFrequencyOptions.map((option) => (
            <Radio
              key={option}
              name={option}
              label={option}
              value={option}
              className={option}
              onUpdate={updateFrequency}
              onError={onError}
            />
          ))}
        </RadioGroup>
      </Field>

      {Frequency?.value === 'Other'
        && (
          <Field
            title={i18n.t('history.pvq.employment.heading.explanation')}
            titleSize="label"
            adjustFor="textarea"
            scrollIntoView={scrollIntoView}
            attribute={`${itemAttribute}.FrequencyOtherExplanation`}
          >
            <Textarea
              {...FrequencyOtherExplanation}
              name="FrequencyOtherExplanation"
              className="frequency-explanation"
              onUpdate={updateFrequencyOtherExplanation}
              onError={onError}
              required={required}
              attribute={`${itemAttribute}.FrequencyOtherExplanation`}
            />
          </Field>
        )}

      <Field
        scrollIntoView={scrollIntoView}
        attribute={`${itemAttribute}.Address`}
        adjustFor="address"
      >
        <PVQLocationUnknown
          {...Address}
          inUsaUnknown={InUsaNotApplicable}
          attribute={`${itemAttribute}.Address`}
          name="foreign-national-address"
          onUpdate={updateAddress} // eslint-disable-line @typescript-eslint/unbound-method
          onUpdateInUsaUnknown={updateInUsaNotApplicablePvq}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          onError={onError}
          required={required}
          geocode
          showInUsaUnknown
          showInternationalStreet
          label={i18n.t(`history.pvq.employment.heading.foreignContactAddress`)}
          domesticTitle={i18n.t(`history.pvq.employment.heading.foreignContactAddessDomesticTitle`)}
          foreignTitle={i18n.t(`history.pvq.employment.heading.foreignContactAddressForeignTitle`)}
        />
      </Field>

      <Field
        title={i18n.t('history.pvq.employment.heading.relationship')}
        scrollIntoView={scrollIntoView}
        attribute={`${itemAttribute}.Relationship`}
      >
        <Text
          {...Relationship}
          name="Relationship"
          className="Relationship"
          onUpdate={updateRelationship}
          onError={onError}
          required={required}
          attribute={`${itemAttribute}.Relationship`}
        />
      </Field>
    </>
  );
};

EmploymentForeignService.defaultProps = {
  Discipline: {},
  DisciplineOtherExplanation: {},
  Country: {},
  Agency: {},
  CivilianOrMilitary: {},
  HighestRank: {},
  Department: {},
  HowAssociated: {},
  DatesServed: {},
  ReasonLeft: {},
  ForeignContacts: {},
  addressBooks: {},
  dispatch: () => { },
  onUpdate: () => { },
  onError: (value, arr: ErrorCode[]) => arr,
};
